import { Button, ConfigProvider, Divider, Modal, Spin, Steps } from "antd";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { use, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { hasStepError, ocfSchema, stepFieldMap } from "./Schema";
import { FormProvider, get, useFieldArray, useForm } from "react-hook-form";
import Applicant from "./steps/Applicant";
import CoApplicant from "./steps/CoApplicant";
import { yupResolver } from "@hookform/resolvers/yup";
import Property from "./steps/Property";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import Costing from "./steps/Costing";
import Payment from "./steps/Payment";
import StepContent from "./steps/StepContent";
import { set } from "date-fns";
import { Grid } from "antd";
import { showNotification } from "../../service/Notification";
import { InfoCircleOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Icon, IconButton, Tab, Tabs } from "@mui/material";
import { useMaterialUIController } from "context";
import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { getLoggedInUser } from "service/AuthService";
import {
  getMaxAllowedStep,
  getStepsByPermissions,
  getStepsByRole,
  getStepsByRoleAndStatus,
  OTF_STATUS,
} from "./steps/utils";
import MDButton from "components/MDButton";

const Form = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [ocfDetailsLoader, setOcfDetailsLoader] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [formStatus, setFormStatus] = useState(null);
  const [user, setUser] = useState({});
  const [ocfDetails, setOcfDetails] = useState({});
  // const LAST_STEP = 7;
  const LAST_STEP = getStepsByRole(user?.roles?.[0]).length - 1;

  const LAST_STEP2 =
    getStepsByPermissions(user?.permissions, ocfDetails?.status || "Draft").length - 1;

  const methods = useForm({
    resolver:
      current == 4 && ocfDetails?.status != "Confirmed" ? yupResolver(ocfSchema) : undefined,
    mode: "onSubmit",
    shouldUnregister: false,
    shouldFocusError: false,
    defaultValues: {
      applicant: {
        mobile2: "",
      },
      coApplicant: {
        available: false, // your switch
        name: "",
        careOfName: "",
        gender: "",
        email: "",
        mobile: "",
        mobile2: "",
        dob: null,
        address: "",
        profession: "",
        aadhar: "",
        pan: "",
        aadharFile: null,
        panFile: null,
      },
      property: {
        isDiscount: false,
        discount: 0,
        plc: 0,
      },
      costing: {
        bookingForm: [{ file: null }],
      },
      payment: [
        {
          type: "",
          amount: "",
          receiptNo: "",
          confirmation: false,
          paymentDate: null,
        },
      ],
      futurePaymentsForm: {
        payments: [],
        bbaSigned: false,
        bbaSignedDate: null,
        remark: "",
      },
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    reset,
    formState: { errors, dirtyFields },
  } = methods;

  const {
    fields: paymentFields,
    append: addPayment,
    remove: removePayment,
  } = useFieldArray({
    control,
    name: "payment",
  });

  const {
    fields: bookingFormFields,
    append: addBookingForm,
    remove: removeBookingForm,
  } = useFieldArray({
    control,
    name: "costing.bookingForm",
  });

  const steps = [
    { title: "Customer" },
    { title: "Co-Applicant" },
    { title: "Property Details" },
    { title: "Costing" },
    { title: "Payment" },
    { title: "Review & Submit" },
    { title: "Collection" },
  ];

  // const stepItems = steps.map((step, index) => {
  //   let status = "wait";
  //   if (index < current) status = "finish";
  //   if (index === current) status = "process";
  //   if (hasStepError(index, errors)) {
  //     status = "error";
  //   }
  //   return {
  //     title: step.title,
  //     status,
  //   };
  // });

  const userRole = user?.roles?.[0]; // USER / CRM / ACCOUNTS
  // const roleSteps = getStepsByRole(userRole);
  const roleSteps = getStepsByRoleAndStatus(userRole, ocfDetails?.status || "Draft");

  const stepsData = getStepsByPermissions(user?.permissions, ocfDetails?.status || "Draft");

  const stepItems = roleSteps.map((step, index) => {
    let statusType = "wait";

    if (index < current) statusType = "finish";
    if (index === current) statusType = "process";
    if (hasStepError(index, errors)) statusType = "error";

    return {
      title: step.title,
      status: statusType,
    };
  });

  const stepItems2 = stepsData.map((step, index) => {
    let statusType = "wait";

    if (index < current) statusType = "finish";
    if (index === current) statusType = "process";
    if (hasStepError(index, errors)) statusType = "error";

    return {
      title: step.title,
      status: statusType,
    };
  });

  // const saveStepDraft = async (stepIndex) => {
  //   const stepFields = stepFieldMap[stepIndex];
  //   const values = getValues();
  //   const changedFields = stepFields.filter((f) => dirtyFields[f]);
  //   if (changedFields.length === 0) {
  //     return true;
  //   }
  //   const payload = {};
  //   changedFields.forEach((f) => {
  //     payload[f] = values[f];
  //   });

  //   console.log(payload);

  //   return true;
  // };

  const stepKeyMap = {
    0: "applicant",
    1: "coApplicant",
    2: "property",
    3: "costing",
    4: "payment",
    5: "review",
    6: "collection",
  };

  const saveData = async (stepIndex, payload) => {
    try {
      setLoading(true);
      const res = await axiosPrivate({
        method: "POST",
        url: `om/saveOcf?stage=${stepIndex + 1}`,
        data: payload,
      });
      setLoading(false);
      if (res?.status === 200 && res?.data?.status === "OK") {
        // showNotification("success", "Success", res?.data?.message);
        return true;
      }
      showNotification("success", "Success", res?.data?.message);

      return false;
    } catch (err) {
      setLoading(false);
      showNotification("error", "Error", err?.response?.data?.message || "Something went wrong!");
      return false; // ✅ Now this actually returns from saveData
    }
  };

  const saveStepDraft = async (stepIndex) => {
    const values = getValues();
    let payload = {};

    switch (stepIndex) {
      case 0:
        payload = { applicant: values.applicant };
        break;
      case 1:
        payload = { coApplicant: values.coApplicant };
        break;
      case 2:
        payload = { propertyForm: values.property };
        break;

      case 3:
        payload = {
          costing: {
            ...values.costing,
            remark: `${
              values?.costing?.remark + "AddedOn: " + dayjs().format("DD MM YYYY HH:mm:ss") || ""
            }`,
          },
        };
        break;

      case 4:
        payload = { payment: values.payment };
        break;

      case 5:
        payload = { futurePaymentsForm: values.futurePaymentsForm };
        break;

      default:
        return true;
    }

    payload.ocfId = id;
    const stepKey = stepKeyMap[stepIndex];
    // const isStepDirty = !!dirtyFields?.[stepKey] && stepIndex != 4;
    // console.log(isStepDirty);
    const isStepDirty = stepIndex === 5 ? false : !!dirtyFields?.[stepKey];
    // const isStepDirty = true; // always save draft on step change, you can change this logic as per your need
    if (isStepDirty) {
      if (stepIndex == 2 && payload.propertyForm?.type && payload.propertyForm?.size) {
        payload.propertyForm.typeSize = {
          type: payload.propertyForm.type,
          size: [payload.propertyForm.size],
        };
      }
      const result = await saveData(stepIndex, payload);
      await trigger();
      if (result) return true;
      else return false;
    } else {
      return true;
    }
  };

  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [showFuturePayment, setShowFuturePayment] = useState(false);

  const next = async () => {
    if (isFormLocked()) {
      setCurrent((p) => p + 1);
      return;
    }

    const success = await saveStepDraft(current);
    if (!success) return;
    await trigger();
    setCurrent((p) => {
      if (p == 4) {
        if (ocfDetails?.status !== "Submitted") {
          setShowSubmitButton(true);
          return p;
        } else {
          return p + 1;
        }
      }
      //  else if (p == 5) {
      //   setShowFuturePayment(true);
      //   return p;
      // }
      else {
        return p + 1;
      }
    });
  };

  const prev = () => {
    setCurrent((p) => p - 1);
    setShowSubmitButton(false);
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const onSubmit = async (data) => {
    if (isFormLocked()) return;
    const valid = await trigger();
    if (!valid) return;
    setShowConfirmModal(true);
  };

  const finalSubmit = async () => {
    if (isFormLocked()) return;

    const apiPayload = getValues();
    apiPayload.ocfId = id;
    if (apiPayload.property?.type && apiPayload.property?.size) {
      apiPayload.property.typeSize = {
        type: apiPayload.property.type,
        size: [apiPayload.property.size],
      };
    }
    const payload = { ...apiPayload, propertyForm: apiPayload.property };
    delete payload.property;

    setLoading(true);
    axiosPrivate({
      method: "POST",
      url: `om/submitOcf`,
      data: payload,
    })
      .then((res) => {
        if (res?.status == 200 && res?.data?.status == "OK") {
          showNotification("success", "Success", res?.data?.message);
        }
        navigate(0); // refresh page to get updated status, you can change this as per your need
      })
      .catch((err) => {
        console.log(err);
        showNotification("error", "Error", err?.response?.data?.message || "Something went wrong!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const [propertyList, setPropertyList] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [propertyConfig, setPropertyConfig] = useState();
  const getPropertyConfig = () => {
    const option = {
      url: `om/masterConfig/ocf`,
      method: "get",
    };
    try {
      axiosPrivate(option)
        .then((response) => {
          setPropertyConfig(response?.data?.data);
        })
        .catch((err) => {
          console.log(err);
        });
    } catch (error) {
      console.log(error);
    }
  };

  const getOcfDetails = (id) => {
    const option = {
      url: `om/ocfDetails?ocfId=${id}`,
      method: "get",
    };
    setOcfDetailsLoader(true);
    try {
      axiosPrivate(option)
        .then((response) => {
          setOcfDetails(response?.data?.data);
          const ocf = response?.data?.data;
          const type = ocf?.propertyResponse?.typeSize?.type || "";
          // Since size is an array in your payload logic, we take the first element
          const size = ocf?.propertyResponse?.typeSize?.size?.[0] || "";
          reset({
            applicant: {
              ...ocf?.applicant,
              mobile2: ocf?.applicant?.mobile2 || "",
              panFile: ocf?.documentIds?.customer_pan
                ? { message: ocf.documentIds.customer_pan }
                : null,
              aadharFile: ocf?.documentIds?.customer_aadhar
                ? { message: ocf.documentIds.customer_aadhar }
                : null,
            },
            coApplicant: {
              ...ocf?.coApplicant,
              panFile: ocf?.documentIds?.coApplicant_pan
                ? { message: ocf.documentIds.coApplicant_pan }
                : null,
              aadharFile: ocf?.documentIds?.coapplicant_aadhar
                ? { message: ocf.documentIds.coapplicant_aadhar }
                : null,
            },
            property: {
              ...ocf?.propertyResponse,
              type,
              size,
              plc: ocf?.propertyResponse?.plc || 0,
              discount: ocf?.propertyResponse?.discount ?? 0,
              isDiscount: !!ocf?.propertyResponse?.isDiscount,
              commissionDiscountUnit: ocf?.propertyResponse?.commissionType,
              dis_approval_file: ocf?.documentIds?.dis_approval_file
                ? { message: ocf.documentIds.dis_approval_file }
                : null,
            },
            costing: {
              ...ocf?.costing,
              bookingForm: Object.keys(ocf?.documentIds || {})
                .filter((key) => key.startsWith("bookingForm_"))
                .map((key) => ({
                  file: {
                    message: ocf.documentIds[key],
                  },
                })),
            },
            payment: ocf?.payment?.map((p, index) => ({
              ...p,
              paymentProof: ocf?.documentIds?.[`paymentProof_${index + 1}`]
                ? { message: ocf.documentIds[`paymentProof_${index + 1}`] }
                : null,

              builderConfirmationFile: ocf?.documentIds?.[`builderConfirmationFile_${index + 1}`]
                ? { message: ocf.documentIds[`builderConfirmationFile_${index + 1}`] }
                : null,
            })),
          });

          setFormStatus(ocf?.status);
          setOcfDetailsLoader(false);
        })
        .catch((err) => {
          console.log("catch errro");
          console.log(err);
          setOcfDetailsLoader(false);
          showNotification(
            "error",
            "Error",
            err?.response?.data?.message || "Something went wrong!"
          );
          setOcfDetails({});
        });
    } catch (err) {
      console.log(err);
      setOcfDetails({});
    }
  };

  const getLoggedIn = async () => {
    const user = await getLoggedInUser();
    setUser(user);
  };
  const getList = () => {
    try {
      axiosPrivate({ method: "GET", url: "om/property" })
        .then((res) => {
          const active = res?.data?.data?.filter((p) => p.active);
          setPropertyList(active || []);
        })
        .catch((err) => {
          console.log(err);
        });
    } catch (error) {
      console.log(error);
    }
  };
  /* ================= FETCH ================= */
  useEffect(() => {
    getPropertyConfig();
    getList();
    if (id) {
      getOcfDetails(id);
    }
    getLoggedIn();
  }, []);

  const isFormLocked = () => {
    return (
      ["Submitted", "Approved", "Confirmed", "Ready To Bill", "Billed", "Collected"].includes(
        formStatus
      ) || !user?.permissions.includes("CREATE_OTF")
    );
  };

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const FormWrapper = ({ disabled, children }) => {
    return (
      <div style={{ pointerEvents: disabled ? "none" : "auto", opacity: disabled ? 0.6 : 1 }}>
        {children}
      </div>
    );
  };

  if (ocfDetailsLoader) {
    return <Spin />;
  }
  if (!ocfDetails || Object.keys(ocfDetails).length === 0) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="60vh"
        textAlign="center"
      >
        <MDBox fontSize={60}>🚫</MDBox>

        <MDTypography variant="h5" fontWeight="medium" mt={2}>
          Access Denied
        </MDTypography>

        <MDTypography variant="body2" color="text" mt={1}>
          You don’t have access to this OTF
        </MDTypography>
      </MDBox>
    );
  }
  return (
    <>
      <MDBox mx={2}></MDBox>
      <div className="page-wrapper">
        <div className="page-header">
          <MDBox
            // mx={2}
            mb={4}
            py={2}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              OTF Form : {ocfDetails?.ocfDisplayId}
            </MDTypography>
            <MDBox display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
              <MDTypography variant="h6" color="white">
                Status :{" "}
                <span
                  style={
                    ocfDetails?.status === OTF_STATUS.REJECTED
                      ? { color: "#f33a3a" }
                      : { color: "inherit" } // default color
                  }
                >
                  {ocfDetails?.status || OTF_STATUS.DRAFT}
                </span>
                <span
                  style={{ marginLeft: "12px ", cursor: "pointer" }}
                  onClick={() => {
                    setShowInfoModal(!showInfoModal);
                  }}
                >
                  <InfoCircleOutlined />
                </span>
              </MDTypography>
              {/* <MDButton
                size="small"
                onClick={() => {
                  setShowInfoModal(!showInfoModal);
                }}
              >
                Info
              </MDButton> */}
              <MDTypography variant="caption" color="white">
                Last Updated On :{" "}
                {ocfDetails?.lastUpdatedOn
                  ? new Date(ocfDetails.lastUpdatedOn).toLocaleString()
                  : "NA"}
              </MDTypography>
            </MDBox>
          </MDBox>
        </div>

        <div className="page-content">
          <FormProvider {...methods}>
            {/* <ConfigProvider componentDisabled={isFormLocked()}> */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <Steps current={current} items={stepItems2} />
              <MDBox
              // sx={{
              //   height: "50dvh",
              //   display: "flex",
              //   flexDirection: "column",
              //   overflow: "hidden", // stop page scroll
              // }}
              >
                <MDBox
                // sx={{
                //   flex: 1,
                //   overflowY: "auto", // ✅ only this scrolls
                //   pr: 1,
                // }}
                >
                  <StepContent
                    id={id}
                    propertyList={propertyList}
                    setValue={setValue}
                    current={current}
                    propertyConfig={propertyConfig}
                    ocfDisplayId={ocfDetails?.ocfDisplayId}
                    paymentFields={paymentFields}
                    addPayment={addPayment}
                    removePayment={removePayment}
                    bookingFormFields={bookingFormFields}
                    addBookingForm={addBookingForm}
                    removeBookingForm={removeBookingForm}
                    isFormLocked={isFormLocked()}
                    ocf={ocfDetails}
                    getOcfDetails={() => {
                      ocfDetails();
                    }}
                  />
                </MDBox>
              </MDBox>
              <Divider />
            </form>
            {/* </ConfigProvider> */}
          </FormProvider>
        </div>

        {/* <div className="page-footer">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {current > 0 ? <Button onClick={prev}>Previous</Button> : <span></span>}
            <p>{current < LAST_STEP2}</p>

            {current < stepsData.length && !showSubmitButton && (
              <Button type="primary" onClick={next} loading={loading}>
                {isFormLocked() ? "Next" : "Save & Next"}
              </Button>
            )}

            {showSubmitButton && !isFormLocked() && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={handleSubmit(onSubmit)}
                loading={loading}
              >
                Submit OCF
              </Button>
            )}
          </div>
        </div> */}

        <div className="page-footer">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {current > 0 ? <Button onClick={prev}>Previous</Button> : <span />}

            {/* Next Button */}
            {current < LAST_STEP2 && !showSubmitButton && (
              <Button type="primary" onClick={next} loading={loading}>
                {isFormLocked() ? "Next" : "Save & Next"}
              </Button>
            )}

            {/* Submit Button */}
            {current === LAST_STEP2 && !isFormLocked() && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={handleSubmit(onSubmit)}
                loading={loading}
              >
                Submit OCF
              </Button>
            )}
          </div>
        </div>
        <Dialog
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          fullScreen={fullScreen}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: darkMode ? "#1a2035" : "#fff", // same as ConfigProvider example
              color: darkMode ? "#fff" : "#000",
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent>
            <MDTypography>Are you sure you want to submit this OCF?</MDTypography>
            <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowConfirmModal(false);
                  finalSubmit();
                }}
                loading={loading}
              >
                Confirm
              </Button>
            </MDBox>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          fullScreen={fullScreen}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: darkMode ? "#1a2035" : "#fff", // same as ConfigProvider example
              color: darkMode ? "#fff" : "#000",
              borderRadius: 2,
            },
          }}
        >
          {/* <DialogTitle>Remarks </DialogTitle> */}

          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: darkMode ? "#fff" : "#000",
              backgroundColor: darkMode ? "#1f2937" : "#f5f5f5",
              borderBottom: `1px solid ${darkMode ? "#475894" : "#d9d9d9"}`,
            }}
          >
            <MDTypography variant="h5">Remarks</MDTypography>

            <IconButton
              onClick={() => setShowInfoModal(false)}
              sx={{ color: darkMode ? "#fff" : "#000" }}
            >
              <Icon>close</Icon>
            </IconButton>
          </DialogTitle>

          {/* <DialogContent style={{ display: "flex", flexDirection: "column" }}>
            <MDTypography variant="text">
              Sales Person Remark : {ocfDetails?.costing?.salesPersonRemark}
            </MDTypography>
            <MDTypography variant="text" style={{ whiteSpace: "pre-line" }}>
              CRM Remark : {ocfDetails?.crmRemark}
            </MDTypography>
            <MDTypography variant="text" style={{ whiteSpace: "pre-line" }}>
              ACCOUNTS Remark :{" "}
              <span style={{ paddingLeft: "12px" }}>{ocfDetails?.accountsRemark} </span>
            </MDTypography>
          </DialogContent> */}

          <DialogContent>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div className="remark-box">
                <span className="remark-title">Sales Person Remark</span>
                <p className="remark-text">
                  {ocfDetails?.costing?.salesPersonRemark || "No remark added"}
                </p>
              </div>

              <div className="remark-box">
                <span className="remark-title">CRM Remark</span>
                <p className="remark-text">{ocfDetails?.crmRemark || "No remark added"}</p>
              </div>

              <div className="remark-box">
                <span className="remark-title">Accounts Remark</span>
                <p className="remark-text">{ocfDetails?.accountsRemark || "No remark added"}</p>
              </div>

              <div className="remark-box">
                <span className="remark-title">Operations Remark</span>
                <p className="remark-text">{ocfDetails?.operationRemark || "No remark added"}</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Form;
